/**
 * Print-ready PDF export for membership card HTML (admin ZIP downloads).
 *
 * Uses html2canvas + jsPDF (RGB). True PDF/X-1a or PDF/X-4 conformance (CMYK, output intents)
 * is not available in-browser — export maximizes sharpness and stability; commercial printers
 * can convert via Adobe Acrobat Preflight or similar if they require PDF/X.
 */

/** Off-screen host width so layout/html2canvas see real dimensions. */
const HOST_WIDTH_PX = 920;

/** Per-card ceiling so the UI cannot stay on "Preparing PDF..." indefinitely. */
export const PDF_RENDER_TIMEOUT_MS = 120000;

/**
 * @returns {Promise<void>}
 */
async function waitForHostAssets(root) {
  const doc = root.ownerDocument || document;
  if (doc.fonts?.ready) {
    await doc.fonts.ready.catch(() => {});
  }
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((resolve) => {
              const done = () => resolve();
              img.addEventListener('load', done, { once: true });
              img.addEventListener('error', done, { once: true });
            })
    )
  );
  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function hasCardMarkup(root) {
  return Boolean(root?.querySelector('.sheet, .id-card, .card-pair'));
}

function waitForCardMarkup(root, timeoutMs = 10000) {
  if (hasCardMarkup(root)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const started = Date.now();
    let settled = false;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      fn();
    };

    const observer = new MutationObserver(() => {
      if (hasCardMarkup(root)) finish(resolve);
    });
    observer.observe(root, { childList: true, subtree: true });

    const poll = () => {
      if (hasCardMarkup(root)) {
        finish(resolve);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        finish(() => reject(new Error('Failed to load ID card HTML for PDF export')));
        return;
      }
      setTimeout(poll, 50);
    };
    poll();
  });
}

/**
 * Mount card HTML in the main document (more reliable than srcdoc iframes).
 *
 * @param {string} htmlString
 * @returns {{ host: HTMLDivElement, source: Element }}
 */
function mountCardHtml(htmlString) {
  const host = document.createElement('div');
  host.className = 'id-card-pdf-export-host';
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${HOST_WIDTH_PX}px`,
    'border:0',
    'opacity:0',
    'pointer-events:none',
    'visibility:hidden',
    'overflow:visible',
  ].join(';');

  const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
  const styleText = parsed.querySelector('style')?.textContent;
  if (styleText) {
    const styleEl = document.createElement('style');
    styleEl.textContent = styleText;
    host.appendChild(styleEl);
  }

  const mount = document.createElement('div');
  mount.innerHTML = parsed.body?.innerHTML || '';
  host.appendChild(mount);

  document.body.appendChild(host);
  const source = host.querySelector('.sheet') || mount;
  return { host, source };
}

/** html2pdf.js options tuned for crisp logos/text on A4 (single card per sheet). */
export function getIdCardPdfExportOptions() {
  return {
    margin: [0, 0, 0, 0],
    image: {
      type: 'png',
      quality: 1,
    },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: HOST_WIDTH_PX,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    },
    pagebreak: { mode: ['css', 'legacy'] },
  };
}

function normalizePdfBlob(value) {
  if (value instanceof Blob && value.size > 0) return value;
  if (value instanceof ArrayBuffer && value.byteLength > 0) {
    return new Blob([value], { type: 'application/pdf' });
  }
  if (ArrayBuffer.isView(value) && value.byteLength > 0) {
    return new Blob([value], { type: 'application/pdf' });
  }
  return null;
}

/**
 * Renders full ID card HTML document string to a single-page PDF Blob (RGB).
 *
 * @param {string} htmlString - Full HTML from buildSingleIdCardHtml()
 * @returns {Promise<Blob>}
 */
export async function renderIdCardHtmlToPdfBlob(htmlString) {
  const html2pdfModule = await import('html2pdf.js');
  const html2pdf = html2pdfModule.default || html2pdfModule;
  const opt = getIdCardPdfExportOptions();

  const { host, source } = mountCardHtml(htmlString);
  try {
    await waitForCardMarkup(host);
    await waitForHostAssets(host);
    const worker = html2pdf().set(opt).from(source);
    await worker.toPdf();
    try {
      const pdf = worker.prop?.pdf;
      if (pdf?.setProperties) {
        pdf.setProperties({
          title: 'NDC TEIN Membership ID Card',
          subject:
            'Print-ready softcopy (RGB). For PDF/X-1a or PDF/X-4, use Adobe Acrobat Preflight or your print vendor.',
          keywords: 'TEIN, NDC, membership, ID card, print',
          creator: 'AtuTein DMS',
        });
      } else if (pdf?.setDocumentProperties) {
        pdf.setDocumentProperties({
          title: 'NDC TEIN Membership ID Card',
          subject:
            'Print-ready softcopy (RGB). For PDF/X-1a or PDF/X-4, use Adobe Acrobat Preflight or your print vendor.',
          keywords: 'TEIN, NDC, membership, ID card, print',
          creator: 'AtuTein DMS',
        });
      }
    } catch (_) {
      /* metadata optional */
    }
    const raw = await worker.outputPdf('blob');
    const blob = normalizePdfBlob(raw);
    if (!blob) {
      throw new Error('PDF export produced an empty file');
    }
    return blob;
  } finally {
    host.remove();
  }
}

/**
 * @param {string} htmlString
 * @param {string} [label] - Member id for timeout error messages
 * @returns {Promise<Blob>}
 */
export function renderIdCardHtmlToPdfBlobWithTimeout(htmlString, label = 'member') {
  return Promise.race([
    renderIdCardHtmlToPdfBlob(htmlString),
    new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`PDF generation timed out for ${label}`)),
        PDF_RENDER_TIMEOUT_MS
      );
    }),
  ]);
}
